import React from 'react';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { BillingHistoryProps } from './types';
import { productNameMap } from '../components/productInfo';
import { getCurrency, formatPrice } from './utils';
import { trackEvent, TrackingEvent } from '@/utils/tracking';
import { reportCustomLog } from '@/utils/logReporter';
import './styles/index.less';

const BillingHistory: React.FC<BillingHistoryProps> = ({
  purchaseRecords,
  subscriptionProducts = [],
  addonProducts = [],
  isLoading = false,
  onStripePortalRedirect,
  stripeUrl,
  hasSubscription = false, // 新增参数：用户是否有订阅计划
  userId,
}) => {
  const { t, i18n } = useTranslation();

  // 获取产品信息的辅助函数
  const getProductInfo = (productId: string) => {
    // 先在订阅产品中查找
    let product = subscriptionProducts.find(p => p.product_id === productId);
    if (!product) {
      // 如果没找到，在addon产品中查找
      product = addonProducts.find(p => p.product_id === productId);
    }

    if (!product) {
      return { name: productNameMap[productId] || productId, price: 0 };
    }

    const currency = getCurrency(i18n.language);
    const priceInfo = product.price_list.find(p => p.currency === currency);
    const price = priceInfo?.price || 0;

    return {
      name: productNameMap[productId] || product.name || productId,
      price,
    };
  };

  const columns: ColumnsType<API.PlanListItem> = [
    {
      title: t('account.date') || 'Date',
      dataIndex: 'start_time',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('account.description') || 'Description',
      dataIndex: 'product_id',
      render: (productId) => {
        const productInfo = getProductInfo(productId);
        return t(productInfo.name) || productInfo.name;
      },
    },
    {
      title: t('account.listPrice') || 'List Price',
      dataIndex: 'product_id',
      render: (productId) => {
        const productInfo = getProductInfo(productId);
        const currency = getCurrency(i18n.language);
        return formatPrice(productInfo.price, currency);
      },
    },
  ];

  return (
    <div className="enhanced-billing-history">
      <div className="billing-history-card">
        <div className="card-header">
          <div className="header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
          </div>
          <div className="header-content">
            <h2 className="card-title">
              {t('account.billingHistory') || 'Billing History'}
            </h2>
            <p className="card-subtitle">
              {t('account.downloadInvoicesAndViewHistory') || 'Download invoices and view payment history'}
            </p>
          </div>
          {hasSubscription && stripeUrl && (
            <div className="header-actions">
              <button
                onClick={() => {
                  trackEvent(TrackingEvent.VISIT_CHECKOUT, {});
                  reportCustomLog('VisitCheckout', userId);
                  window.open(stripeUrl, '_blank');
                }}
                className="stripe-button"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                <span>{t('account.viewInStripe') || 'View in Stripe'}</span>
              </button>
            </div>
          )}
        </div>

        <div className="card-content">
          {hasSubscription && stripeUrl && (
            <div className="info-banner">
              <div className="banner-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4"/>
                  <path d="M12 8h.01"/>
                </svg>
              </div>
              <div className="banner-content">
                <h4 className="banner-title">
                  {t('account.invoiceDownload') || 'Invoice Download'}
                </h4>
                <p className="banner-description">
                  {t('account.stripeInvoiceInfo') || 'To download individual invoices, please use the "View in Stripe" button above.'}
                </p>
              </div>
            </div>
          )}

          <div className="table-container">
            <Table
              rowKey="user_product_id"
              pagination={false}
              columns={columns}
              dataSource={purchaseRecords}
              loading={isLoading}
              className="custom-table"
              bordered={false}
              locale={{ emptyText: t('NoData') || 'No data available' }}
            />
          </div>
          {/* 金额列为产品目录标价，优惠/折扣后的实付金额仅 Stripe 侧有数据 */}
          <p className="amount-note">
            {t('account.actualChargeNote') || 'Actual charged amounts (including discounts) can be viewed via "View in Stripe".'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BillingHistory;